// Shared date helpers.
//
// "Report day" is the operator's LOCAL calendar day (PO 2026-06-27), NOT a UTC
// slice. Use localDay() everywhere a daily-report / survey / timeline day-date is
// CREATED so the stored date matches the day the user sees (the swim-lane Time
// Log groups by local day, and the ?at= report-launch join keys off it).
//
// MIGRATION: records created before this used a UTC day slice
// (`toISOString().slice(0,10)` or `.split("T")[0]`). In a negative-UTC offset, a
// late-evening record can read one day earlier than its local day. That is
// pre-existing data only — new records are local. No automatic rewrite (Charter:
// no silent data mutation).

/** Local calendar day as `YYYY-MM-DD` (zero-padded). Defaults to now. */
export function localDay(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** ISO 8601 with the LOCAL UTC offset (UTC + offset), e.g.
 *  `2026-06-27T22:00:00.000-05:00`. This is the canonical "store time as UTC +
 *  offset" format (PO 2026-06-27): it pins the absolute instant AND preserves
 *  the local zone, so the local wall-clock is recoverable without ambiguity —
 *  unlike `toISOString()`, which collapses to `Z` and drops the offset.
 *  `new Date(toOffsetISO(d)).getTime() === d.getTime()`. Defaults to now. */
export function toOffsetISO(d: Date = new Date()): string {
  const p = (n: number, len = 2): string =>
    String(Math.abs(n)).padStart(len, "0");
  const tz = -d.getTimezoneOffset(); // minutes east of UTC (UTC-5 → -300)
  const sign = tz >= 0 ? "+" : "-";
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` +
    `.${p(d.getMilliseconds(), 3)}` +
    `${sign}${p(Math.floor(Math.abs(tz) / 60))}:${p(Math.abs(tz) % 60)}`
  );
}
